/**
 * Cycle de vie d'une course.
 *
 *  DRAFT ─▶ SEARCHING ─▶ ACCEPTED ─▶ DRIVER_ARRIVED ─▶ IN_PROGRESS ─▶ COMPLETED
 *              │                                                   │
 *              └──▶ NO_DRIVER                                       └──▶ CANCELLED
 */
export enum RideStatus {
	/** Créée mais pas encore diffusée (utilisée pour l'estimation). */
	DRAFT = 'DRAFT',
	/**
	 * Réservée pour plus tard : la diffusion n'a pas encore commencé.
	 *
	 * Une course de nuit commandée la veille ne doit mobiliser personne avant
	 * l'heure : le dispatch démarre quelques minutes avant la prise en charge, ou
	 * l'exploitation désigne un chauffeur à l'avance.
	 */
	SCHEDULED = 'SCHEDULED',
	/** Diffusée aux chauffeurs proches, en attente d'acceptation. */
	SEARCHING = 'SEARCHING',
	/** Un chauffeur a accepté, il roule vers le point de prise en charge. */
	ACCEPTED = 'ACCEPTED',
	/** Le chauffeur est sur place et attend le client. */
	DRIVER_ARRIVED = 'DRIVER_ARRIVED',
	/** Client à bord, trajet en cours. */
	IN_PROGRESS = 'IN_PROGRESS',
	/** Déposé à destination. */
	COMPLETED = 'COMPLETED',
	/** Annulée (par le client, le chauffeur, l'exploitation ou le système). */
	CANCELLED = 'CANCELLED',
	/** Aucun chauffeur n'a accepté dans le délai imparti. */
	NO_DRIVER = 'NO_DRIVER',
}

/** Statuts pour lesquels la course est encore « vivante » (à suivre en temps réel). */
export const ACTIVE_RIDE_STATUSES: RideStatus[] = [
	// Une réservation est une course en cours de vie : elle doit apparaître dans
	// « mes courses », interdire d'en commander une seconde, et se voir en
	// exploitation bien avant l'heure du départ.
	RideStatus.SCHEDULED,
	RideStatus.SEARCHING,
	RideStatus.ACCEPTED,
	RideStatus.DRIVER_ARRIVED,
	RideStatus.IN_PROGRESS,
];

/** Statuts terminaux : plus aucune transition possible. */
export const FINAL_RIDE_STATUSES: RideStatus[] = [
	RideStatus.COMPLETED,
	RideStatus.CANCELLED,
	RideStatus.NO_DRIVER,
];

/**
 * Disponibilité d'un chauffeur.
 *
 * `AVAILABLE` est le seul statut qui rend le chauffeur éligible au dispatch.
 * `EN_ROUTE` = en course (il va chercher le client), `ON_BOARD` = en charge
 * (client à bord).
 */
export enum DriverStatus {
	/** Hors service : pas de géolocalisation transmise, pas de propositions. */
	OFFLINE = 'OFFLINE',
	/** Disponible : reçoit les propositions de course. */
	AVAILABLE = 'AVAILABLE',
	/** Pause (repas, carburant…) : en ligne mais non éligible. */
	PAUSED = 'PAUSED',
	/** En course : a accepté une course et roule vers le client. */
	EN_ROUTE = 'EN_ROUTE',
	/** En charge : le client est à bord. */
	ON_BOARD = 'ON_BOARD',
}

/** Statuts pour lesquels le chauffeur peut recevoir une proposition. */
export const DISPATCHABLE_DRIVER_STATUSES: DriverStatus[] = [DriverStatus.AVAILABLE];

/** Réponse d'un chauffeur à une proposition de course. */
export enum RideOfferStatus {
	PENDING = 'PENDING',
	ACCEPTED = 'ACCEPTED',
	DECLINED = 'DECLINED',
	/** Un autre chauffeur a été plus rapide. */
	LOST = 'LOST',
	/** Le chauffeur n'a pas répondu à temps. */
	EXPIRED = 'EXPIRED',
	/** La course a été annulée avant réponse. */
	CANCELLED = 'CANCELLED',
}

/** Origine de la commande — sert aux statistiques et au comportement post-course. */
export enum RideSource {
	APP = 'APP',
	WEB = 'WEB',
	KIOSK = 'KIOSK',
	BACKOFFICE = 'BACKOFFICE',
}

/** Qui a annulé. */
export enum CancelledBy {
	CLIENT = 'CLIENT',
	DRIVER = 'DRIVER',
	OPERATOR = 'OPERATOR',
	SYSTEM = 'SYSTEM',
}
